import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import queryStringParser from "query-string";
import {
  IContactInfo,
  TravelerSelectStep,
  DesktopPopupModal,
  CheckoutStepper,
  ButtonWrap,
  IconName,
  Icon,
  InformationalModal as TreesModal,
  usePrevious,
  ApprovalBanner,
  ApprovalReasonTextarea,
  TripDescription,
  TripPurposePanel,
} from "halifax";
import { RouteComponentProps } from "react-router";
import {
  CallState,
  IPerson,
  ITravelerStepErrors,
  ListingCollectionEnum,
} from "redmond";
import clsx from "clsx";
import { CDN_ASSETS, getCurrentCorpAccount, getConfigTenant, getIsEMEligible, isCorpTenant, isVesperTenant } from "@capone/common";

// components
import { Box, Typography, Button } from "@material-ui/core";
import { DesktopShop } from "../../../../shop/components/DesktopShop";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import { AGENT_FEE, PaymentCard } from "../../index";
import PremierCollectionCheckoutBenefits from "../../PremierCollectionCheckoutBenefits";
import { SelectedVacationRentalSummary } from "../../SelectedVacationRentalSummary";
import { VacationRentalBookTravellerSection } from "../VacationRentalBookTravellerSection";
import { ListingTermsInfo } from "../../../../vacation-rental-shop/components/ListingTermsInfo";
import { VacationRentalPriceBreakdown } from "../../VacationRentalPriceBreakdownCard";

// types, constants
import { DesktopVacationRentalsBookWorkflowConnectorProps } from "./container";
import {
  BOOK_BUTTON_TEXT,
  REQUIRED_DETAILS_TEXT,
  AGENT_DETAILS_MODAL_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_SUBTITLE,
} from "./textConstants";

// context
import { ClientContext } from "../../../../../App";

// helpers, utils
import { fetchCustomerDetails } from "../../../../../api/v0/customer/fetchCustomerDetails";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import {
  PATH_HOME,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
} from "../../../../../utils/paths";

// styles
import "./styles.scss";
import {
  AVAILABLE,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../../context/experiments";
import { useExperimentIsVariant } from "@capone/experiments";
import { config } from "../../../../../api/config";
import { getRewardsAccounts } from "../../../../rewards/reducer/selectors";

export interface IDesktopVacationRentalsBookWorkflowProps
  extends RouteComponentProps,
  DesktopVacationRentalsBookWorkflowConnectorProps {
  isTreesModalExperiment: boolean;
  isTravelWalletOfferExperiment: boolean;
  isCreditAndOfferStackingExperimentV1: boolean;
  isTravelCreditHistoryExperiment: boolean;
  primaryTraveler?: IPerson;
}

export const DesktopVacationRentalsBookWorkflow = ({
  primaryTraveler,
  setContactInfo,
  combinedBookingSteps,
  reservation,
  history,
  email,
  phoneNumber,
  isTravelerStepComplete,
  isContactStepComplete,
  priceQuoteErrors,
  confirmationDetailsErrors,
  fetchApplicableTravelWalletItems,
  fetchTravelWalletCreditHistory,
  largestEarnAccount,
  selectedHome,
  isTreesModalExperiment,
  isTravelWalletOfferExperiment,
  isCreditAndOfferStackingExperimentV1,
  isTravelCreditHistoryExperiment,
  isBookingValid,
  allStepsCompleted,
  priceQuote,
  scheduleVacationRentalPriceQuote,
  setUserSelectedPassengerIds,
  scheduleVacationRentalsBook,
  addMainGuest,
  addMainGuestCallState,
  userSelectedTraveler,
  setNotifyIfShopAndBookPriceDifferFlag,
  setTripPurpose,
  setTripDescription,
}: IDesktopVacationRentalsBookWorkflowProps) => {
  const clientContext = useContext(ClientContext);
  const { sessionInfo, isAgentPortal, policies } = clientContext;

  const isFirstStepCompletedInCheckout =
    isContactStepComplete && isTravelerStepComplete && !!userSelectedTraveler;

  const isVesper = isVesperTenant(getConfigTenant());

  const [saveButtonClicked, setSaveButtonClicked] = useState(false);
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
  });
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);

  // TODO: remove this when we decide if using text message - https://hopchat.slack.com/archives/C01FX0M22MV/p1620146159000600
  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: phoneNumber || "",
    email: email || sessionInfo?.userInfo?.email || "",
  });
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    useState<boolean>(true);
  const [disableEditTravelerInfo, setDisableEditTravelerInfo] = useState(
    !!primaryTraveler
  );
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [approvalRequestReason, setApprovalRequestReason] =
    useState<string>("");
  const prevCustomerContactLoading = usePrevious(customerDetailsLoading);

  const hasPolicyCompliance = selectedHome && "corporateTravel" in selectedHome;
  const isInPolicy =
    selectedHome?.corporateTravel?.policyCompliance?.isInPolicy ?? true;

  const expState = useExperiments();

  const isXmodRedesign = useMemo(
    () =>
      getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
      AVAILABLE,
    [expState.experiments]
  );

  const isTripPurposeXpEnabled = useExperimentIsVariant(
    "corp-trip-purpose",
    AVAILABLE
  );

  const isExpenseManagementExperiment = useExperimentIsVariant(
    "corp-expense-management",
    AVAILABLE
  );

  const rewardsAccounts = useSelector(getRewardsAccounts);
  const accountForEMEligibility = useMemo(() => {
    if (isCorpTenant(config?.TENANT)) {
      const corpAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);
      return corpAccount || largestEarnAccount;
    }
    return largestEarnAccount;
  }, [rewardsAccounts, sessionInfo, largestEarnAccount, config?.TENANT]);

  const isEMEligible = getIsEMEligible(accountForEMEligibility);

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>, isCharLimitError: boolean) => {
      setApprovalRequestReason(e.target.value);
      if (isCharLimitError) {
        trackEvent({
          eventName: "approval_reason_char_limit_error",
          properties: {
            funnel: "vacation-rentals",
          },
        });
      }
    },
    []
  );

  // TODO: VR - Connect this to real schedule Vacation Rental Bool endpoints
  const onBookVacationRentals = () => {
    const queryString = queryStringParser.parse(history.location.search);
    !!priceQuote
      ? scheduleVacationRentalsBook({
        agentFee: isAgentPortal ? AGENT_FEE : 0,
        isRecommended: queryString.recommended === "true",
        approvalRequestReason: approvalRequestReason,
      })
      : scheduleVacationRentalPriceQuote({
        history,
        emailAddress: email ?? "",
        agentFee: isAgentPortal ? AGENT_FEE : 0,
      });
  };

  useEffect(() => {
    if (reservation) {
      history.push(PATH_VACATION_RENTALS_BOOK_CONFIRMATION);
    }
  }, [reservation]);

  useEffect(() => {
    if (priceQuoteErrors.length > 0 || confirmationDetailsErrors.length > 0) {
      setDisableEditTravelerInfo(false);
    }
  }, [priceQuoteErrors, confirmationDetailsErrors]);

  // Kick off the initial price quote once, but only after the listing has
  // loaded. In the replatform flow it loads from the URL a moment later, and
  // quoting before it is ready fails silently.
  const initialPriceQuoteScheduled = useRef(false);
  useEffect(() => {
    if (
      !customerDetailsLoading &&
      email &&
      selectedHome &&
      !initialPriceQuoteScheduled.current
    ) {
      initialPriceQuoteScheduled.current = true;
      scheduleVacationRentalPriceQuote({
        history,
        emailAddress: email,
        agentFee: isAgentPortal ? AGENT_FEE : 0,
        pollQuoteOnly: true,
      });
    }
  }, [customerDetailsLoading, email, selectedHome]);

  useEffect(() => {
    if (priceQuote && email) {
      addMainGuest({
        emailAddress: email,
        mainGuest: userSelectedTraveler
          ? { ...userSelectedTraveler, phoneNumber }
          : null,
      });
    }
  }, [priceQuote]);

  useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(
          contactInfo?.email || "",
          `${details?.phoneNumber?.includes("+")
            ? ""
            : contactInfo?.countryCode ?? "+1"
          }${details?.phoneNumber}` || "" // We default to "+1" in `PhoneInputField` if there isn't a countrycode saved
        );
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
    isTravelWalletOfferExperiment && fetchApplicableTravelWalletItems(true);
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
  }, []);

  useEffect(() => {
    if (
      !customerDetailsLoading &&
      sessionInfo &&
      (prevCustomerContactLoading !== customerDetailsLoading || // Note: Only check to set disable edit traveler info as false after first render (after customer info loads) or after user hits save & continue
        saveButtonClicked)
    ) {
      if (
        !primaryTraveler ||
        !contactInfo?.email ||
        !contactInfo?.phoneNumber
      ) {
        setDisableEditTravelerInfo(false);
      } else {
        setUserSelectedPassengerIds({
          userSelectedPassengerIds: [primaryTraveler.id],
        });
        setDisableEditTravelerInfo(true);
      }
    }
  }, [
    primaryTraveler,
    customerDetailsLoading,
    contactInfo,
    sessionInfo,
    prevCustomerContactLoading,
    saveButtonClicked,
  ]);

  const renderLogo = (
    <ButtonWrap
      className="logo"
      onClick={() => {
        history.push(PATH_HOME);
      }}
    >
      {clientContext.logo}
    </ButtonWrap>
  );

  const handleBookingInfoClick = useCallback(() => {
    setDetailsModalOpen(!detailsModalOpen);
  }, [detailsModalOpen]);

  const summaryPanelActions = isAgentPortal ? (
    <Typography
      role="button"
      variant="h6"
      className="view-premier-collection-information"
      onClick={handleBookingInfoClick}
    >
      {AGENT_DETAILS_MODAL_TEXT}
    </Typography>
  ) : null;

  if (!selectedHome) {
    return null;
  }

  return (
    <Box
      className={clsx("desktop-vacation-rentals-book-workflow-root", {
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      {detailsModalOpen && (
        <DesktopPopupModal
          open={detailsModalOpen}
          onClose={handleBookingInfoClick}
          className="desktop-vacation-rentals-book-details-modal"
        >
          <DesktopShop hideRightColumn hideProgressbar />
        </DesktopPopupModal>
      )}
      <CheckoutStepper
        steps={combinedBookingSteps}
        headerCopy={BOOK_BUTTON_TEXT}
        subHeaderCopy={REQUIRED_DETAILS_TEXT}
        logo={renderLogo}
        className={clsx("b2b", "combined-step")}
      />
      <Box className="desktop-vacation-rentals-book-workflow-container">
        <Box className={clsx("checkout-template-column", "left")}>
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "vacation-rental"
            )}
          >
            <SelectedVacationRentalSummary
              className={clsx(
                selectedHome.listing.listingCollection ===
                  ListingCollectionEnum.Lifestyle
                  ? "lifestyle-collection"
                  : "premier-collection"
              )}
              actions={summaryPanelActions}
              onUpdateDates={() => {
                setNotifyIfShopAndBookPriceDifferFlag(false);
                scheduleVacationRentalPriceQuote({
                  history,
                  emailAddress: email ?? "",
                  agentFee: isAgentPortal ? AGENT_FEE : 0,
                });
                // Travel wallet will be refreshed automatically after price quote completes
              }}
              onUpdateOccupancy={() => {
                setNotifyIfShopAndBookPriceDifferFlag(false);
                scheduleVacationRentalPriceQuote({
                  history,
                  emailAddress: email ?? "",
                  agentFee: isAgentPortal ? AGENT_FEE : 0,
                });
                // Travel wallet will be refreshed automatically after price quota completes
              }}
            />
          </Box>

          {isCorpTenant(config.TENANT) && isTripPurposeXpEnabled && !isExpenseManagementExperiment && (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "vacation-rental"
              )}
            >
              <TripPurposePanel setTripPurpose={setTripPurpose} />
            </Box>
          )}

          {isTravelWalletOfferExperiment &&
            !isCreditAndOfferStackingExperimentV1 && <TravelOfferSelection />}

          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "traveler"
            )}
          >
            <VacationRentalBookTravellerSection
              disableEditTravelerInfo={disableEditTravelerInfo}
              setDisableEditTravelerInfo={setDisableEditTravelerInfo}
              travelerWorkflowStep={travelerWorkflowStep}
              setTravelerWorkflowStep={setTravelerWorkflowStep}
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              saveButtonClicked={saveButtonClicked}
              contactInfo={contactInfo}
              setContact={setContact}
              customerDetailsLoading={customerDetailsLoading}
              isFirstStepCompletedInCheckout={isFirstStepCompletedInCheckout}
              setSaveButtonClicked={setSaveButtonClicked}
            />
          </Box>
          <Box
            className={clsx(
              "checkout-template-card-content-container",
              "credit"
            )}
          >
            <PaymentCard disabled={false} />
            {isTreesModalExperiment && (
              <TreesModal
                image={CDN_ASSETS.capone.trees.confirmation}
                header={TREES_MODAL_HEADER}
                title={TREES_MODAL_TITLE}
                onClose={() => setTreeModalOpen(false)}
                subtitle={TREES_MODAL_SUBTITLE}
                icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
                openModal={treeModalOpen}
                setOpenModal={setTreeModalOpen}
                modalLinkCopy={TREES_MODAL_CTA_TEXT}
                modalButtonCopyStartIcon={
                  <Icon className="trees-icon" name={IconName.TreesIcon} />
                }
                boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
              />
            )}
          </Box>
          <ListingTermsInfo selectedListing={selectedHome} />
          {isCorpTenant(config.TENANT) && isTripPurposeXpEnabled && isExpenseManagementExperiment && (
            <Box
              className={clsx(
                "checkout-template-card-content-container",
                "vacation-rental"
              )}
            >
              <TripDescription
                setTripDescription={setTripDescription}
                isEMEligible={isEMEligible}
              />
            </Box>
          )}
        </Box>
        <Box className={clsx("checkout-template-column", "right")}>
          <Box className="checkout-template-column-right-wrapper">
            {priceQuote != null && (
              <Box className="vacation-rentals-book-right-content">
                <Box
                  className={clsx(
                    "checkout-template-card-content-container",
                    "payment-break-down"
                  )}
                >
                  <VacationRentalPriceBreakdown />
                </Box>
                {hasPolicyCompliance && !isInPolicy && (
                  <>
                    <ApprovalReasonTextarea
                      value={approvalRequestReason ?? ""}
                      className="approval-reason"
                      onChange={handleReasonChange}
                      corporateTravel={selectedHome?.corporateTravel}
                      productType="home"
                      limit={policies?.hotels.policies[0].maxPricePerNight}
                      label="Approval reason (required)"
                    />
                    <ApprovalBanner isApprovalRequired={!isInPolicy} />
                  </>
                )}
                <Box className="confirm-book-button-container">
                  <Button
                    className="confirm-book-button"
                    disabled={
                      !isBookingValid ||
                      !allStepsCompleted ||
                      addMainGuestCallState != CallState.Success ||
                      Boolean(
                        hasPolicyCompliance &&
                        !isInPolicy &&
                        !approvalRequestReason.trim()
                      )
                    }
                    onClick={onBookVacationRentals}
                  >
                    {BOOK_BUTTON_TEXT}
                  </Button>
                </Box>
              </Box>
            )}
            {largestEarnAccount && largestEarnAccount.earn.hotelsMultiplier && (
              <PremierCollectionCheckoutBenefits
                variant={
                  selectedHome.listing.listingCollection ===
                    ListingCollectionEnum.Lifestyle
                    ? "lifestyle-collection"
                    : "premier-collection"
                }
                largestEarnAccount={largestEarnAccount}
                isVR
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
